import * as React from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import { getProgramCatalog } from '../../../api/programs';
import { ProgramCatalogEntry } from '../../../api/programs.interface';
import { Select, SelectOption } from '../../../components/common/Select';
import { buildProgramSchedulePath } from '../../../components/programs/programs.utils';

interface UpdateTrialPeriodComponentProps {
    processingProgram: boolean;
    processProgram: (programKey: string) => void;
}

const ProcessPrograms: React.FC<UpdateTrialPeriodComponentProps> = ({ processProgram, processingProgram }) => {
    const [catalogItems, setCatalogItems] = React.useState<ProgramCatalogEntry[]>([]);
    const [loadingCatalog, setLoadingCatalog] = React.useState(false);
    const [catalogError, setCatalogError] = React.useState<string | undefined>();
    const [selectedProgram, setSelectedProgram] = React.useState<SelectOption<string | undefined, ProgramCatalogEntry>>({ label: '', value: undefined });

    React.useEffect(() => {
        let active = true;

        const loadCatalog = async () => {
            try {
                setLoadingCatalog(true);
                setCatalogError(undefined);
                const items = await getProgramCatalog();

                if (!active) {
                    return;
                }

                setCatalogItems(items);
            } catch (error: any) {
                if (!active) {
                    return;
                }

                setCatalogError(error?.message || 'Unable to load the program catalog.');
            } finally {
                if (active) {
                    setLoadingCatalog(false);
                }
            }
        };

        loadCatalog();

        return () => {
            active = false;
        };
    }, []);

    const catalogOptions = React.useMemo(() => {
        return catalogItems.map((item) => ({
            item,
            label: `${item.displayName} (${item.key})`,
            value: item.key,
        }));
    }, [catalogItems]);

    const onProcessProgram = React.useCallback(() => {
        if (!selectedProgram.value) {
            return;
        }

        processProgram(selectedProgram.value);
    }, [processProgram, selectedProgram]);


    return (
        <Card sx={{ maxWidth: 600, mt: 5 }}>
            <CardContent>
                <Typography variant="h5">Process Programs</Typography>
                {(processingProgram || loadingCatalog) && (
                       <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 2 }}>
                            <CircularProgress />
                        </Box>
                )}
                {!processingProgram && !loadingCatalog && (
                    <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 2 }}>
                        {catalogError && <Alert severity="error">{catalogError}</Alert>}
                        <Select 
                            label="Program"
                            items={catalogOptions} value={selectedProgram}
                            onItemSelected={setSelectedProgram}
                        />
                        {selectedProgram.item && (
                            <Typography color="text.secondary" variant="body2">
                                Schedule: {buildProgramSchedulePath(selectedProgram.item.key)} • Weeks: {selectedProgram.item.weeks} • {selectedProgram.item.live ? 'Live' : 'Draft'}
                            </Typography>
                        )}
                        <Button variant="contained" color="primary" onClick={onProcessProgram}>
                            Process
                        </Button>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default ProcessPrograms;
